"use client";

import { useMemo } from "react";
import {
  CopyButton,
  ErrorNotice,
  Notice,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { RESULT_CARD, WIDGET_H3 } from "@/components/tool-kit/styles";
import {
  decimalText,
  pointValueText,
  shown,
} from "@/components/tools/calculus/format";
import { FunctionGraph } from "@/components/tools/calculus/function-graph";
import { MathTable } from "@/components/tools/calculus/math-table";
import { MathView } from "@/components/tools/calculus/math-view";
import type { Constant } from "@/components/tools/calculus/read-constant";
import type {
  TAYLOR_SERIES_CALCULATOR_STRINGS,
  TaylorView,
} from "@/components/tools/taylor-series-calculator";
import type { useToolNumber } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { isNumEqual } from "@/lib/tool-kit/calculus/ast";
import { compile } from "@/lib/tool-kit/calculus/evaluate";
import { autoWindow } from "@/lib/tool-kit/calculus/plot";
import { toLatex, toText } from "@/lib/tool-kit/calculus/print";
import {
  evaluateTaylor,
  remainderBound,
  type TaylorTerm,
} from "@/lib/tool-kit/calculus/taylor";
import { CHART_COLORS } from "@/lib/tool-kit/palette";

type Strings = typeof TAYLOR_SERIES_CALCULATOR_STRINGS;
type Fmt = ReturnType<typeof useToolNumber>;
type OkView = Extract<TaylorView, { kind: "ok" }>;
type OkResult = Extract<OkView["result"], { kind: "ok" }>;

function rowFor(
  term: TaylorTerm,
  fmt: Fmt,
  strings: Strings,
  decimals: boolean
) {
  return [
    String(term.k),
    shown(term.derivative, decimals),
    pointValueText(term.atCentre, fmt, strings.undefinedHere, decimals),
    term.coefficient
      ? pointValueText(
          {
            exact: term.coefficient,
            symbolic: term.coefficient,
            value: term.coefficientValue,
          },
          fmt,
          strings.undefinedHere,
          decimals
        )
      : decimalText(fmt, term.coefficientValue),
  ];
}

function Approximation({
  view,
  result,
  point,
  strings,
  fmt,
}: {
  view: OkView;
  result: OkResult;
  point: Constant;
  strings: Strings;
  fmt: Fmt;
}) {
  const { centre } = view;
  const x = point.value;
  const polynomial = evaluateTaylor(result.terms, centre.value, x);
  const f = compile(view.ast, "x");
  const bound = remainderBound(
    view.ast,
    "x",
    result.nextDerivative,
    result.terms,
    centre.value,
    x
  );
  const actual = f(x);
  const far = Math.abs(x - centre.value) > 2.5;
  return (
    <div className="space-y-3">
      <h3 className={WIDGET_H3}>
        {t(strings.approxHeading, { point: shown(point.ast, view.decimals) })}
      </h3>
      <StatGrid>
        <StatCard
          label={strings.approxPolynomial}
          value={decimalText(fmt, polynomial)}
        />
        <StatCard
          label={strings.approxActual}
          value={
            Number.isFinite(actual)
              ? decimalText(fmt, actual)
              : strings.undefinedHere
          }
        />
        <StatCard
          emphasis
          label={strings.approxError}
          value={bound ? decimalText(fmt, bound.actual) : strings.undefinedHere}
        />
        <StatCard
          label={strings.approxBound}
          value={bound ? decimalText(fmt, bound.bound) : strings.undefinedHere}
        />
      </StatGrid>
      {bound ? (
        <p className="text-muted-foreground text-sm">
          {t(strings.approxBoundNote, {
            max: decimalText(fmt, bound.maxDerivative),
          })}
        </p>
      ) : null}
      {far ? <Notice>{strings.approxFar}</Notice> : null}
    </div>
  );
}

function Graphs({
  view,
  result,
  point,
  halfWidth,
  strings,
  fmt,
}: {
  view: OkView;
  result: OkResult;
  point: Constant | null;
  halfWidth: number;
  strings: Strings;
  fmt: Fmt;
}) {
  const { centre } = view;
  const n = result.terms.length - 1;
  const f = useMemo(() => compile(view.ast, "x"), [view.ast]);
  const polynomial = useMemo(
    () => (x: number) => evaluateTaylor(result.terms, centre.value, x),
    [result.terms, centre.value]
  );
  const error = useMemo(
    () => (x: number) => Math.abs(f(x) - polynomial(x)),
    [f, polynomial]
  );
  const xMin = centre.value - halfWidth;
  const xMax = centre.value + halfWidth;
  // The polynomial runs away from f far from the centre; size the window to f.
  const window = useMemo(() => autoWindow([f], xMin, xMax), [f, xMin, xMax]);
  const errorWindow = useMemo(
    () => autoWindow([error], xMin, xMax),
    [error, xMin, xMax]
  );
  const centreText = shown(centre.ast, view.decimals);
  const marks = point
    ? [
        {
          x: point.value,
          y: f(point.value),
          color: CHART_COLORS[7],
          label: t(strings.pointLabelText, {
            x: fmt(point.value, { maximumFractionDigits: 3 }),
            y: fmt(f(point.value), { maximumFractionDigits: 3 }),
          }),
        },
      ]
    : [];
  return (
    <>
      <div className="space-y-3">
        <h3 className={WIDGET_H3}>{strings.graphHeading}</h3>
        <FunctionGraph
          downloadPngLabel={strings.downloadPng}
          downloadSvgLabel={strings.downloadSvg}
          filename="taylor-polynomial-graph"
          points={marks}
          series={[
            { f, color: CHART_COLORS[0], label: strings.seriesFunction },
            {
              f: polynomial,
              color: CHART_COLORS[3],
              dashed: true,
              label: t(strings.seriesPolynomial, { n }),
            },
          ]}
          title={t(strings.graphTitle, { n, centre: centreText })}
          window={window}
        />
      </div>
      <div className="space-y-3">
        <h3 className={WIDGET_H3}>{strings.errorHeading}</h3>
        <FunctionGraph
          downloadPngLabel={strings.downloadPng}
          filename="taylor-error-graph"
          series={[
            { f: error, color: CHART_COLORS[5], label: strings.seriesError },
          ]}
          title={t(strings.errorTitle, { centre: centreText })}
          window={errorWindow}
        />
      </div>
    </>
  );
}

export function TaylorResults({
  view,
  point,
  halfWidth,
  strings,
  fmt,
}: {
  view: OkView;
  point: Constant | null;
  halfWidth: number;
  strings: Strings;
  fmt: Fmt;
}) {
  const { result, centre, decimals } = view;
  const centreText = shown(centre.ast, decimals);
  if (result.kind !== "ok") {
    return (
      <ErrorNotice>
        {t(strings.undefinedAtCentre, {
          order: result.order,
          centre: centreText,
        })}
      </ErrorNotice>
    );
  }
  const n = result.terms.length - 1;
  const isMaclaurin = isNumEqual(centre.ast, 0);
  const options = { decimals };
  const heading = isMaclaurin
    ? t(strings.maclaurinHeading, { n })
    : t(strings.taylorHeading, { n, centre: centreText });
  return (
    <>
      <div className={`${RESULT_CARD} space-y-3`}>
        <h3 className={WIDGET_H3}>{heading}</h3>
        <MathView
          block
          label={`T${n}(x) = ${shown(result.polynomial, decimals)}`}
          latex={`T_{${n}}(x) = ${toLatex(result.polynomial, options)}`}
        />
        <p className="text-muted-foreground text-sm">{strings.formulaLine}</p>
        <div className="flex flex-wrap gap-2">
          <CopyButton
            copiedLabel={strings.copied}
            label={strings.copyPolynomial}
            small
            text={toText(result.polynomial, options)}
          />
          <CopyButton
            copiedLabel={strings.copied}
            label={strings.copyLatex}
            small
            text={toLatex(result.polynomial, options)}
          />
        </div>
      </div>
      {result.truncated ? <Notice>{t(strings.truncated, { n })}</Notice> : null}
      <div className="space-y-2">
        <h3 className={WIDGET_H3}>{strings.tableHeading}</h3>
        <MathTable
          head={[
            strings.columnOrder,
            strings.columnDerivative,
            strings.columnAtCentre,
            strings.columnCoefficient,
          ]}
          rows={result.terms.map((term) =>
            rowFor(term, fmt, strings, decimals)
          )}
        />
      </div>
      {point ? (
        <Approximation
          fmt={fmt}
          point={point}
          result={result}
          strings={strings}
          view={view}
        />
      ) : null}
      <Graphs
        fmt={fmt}
        halfWidth={halfWidth}
        point={point}
        result={result}
        strings={strings}
        view={view}
      />
    </>
  );
}
